import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAsync, useCountdown, formatRoundDate, formatResultAt, money2, pushToast } from "../lib";
import { Countdown, ResultChips, Skeleton, FlagBadge } from "../ui";
import { useAuth } from "../../contexts/JWTContext";
import AuthForm from "../AuthForm";
import Slider from "../../components/Slider";
import { RESULTS_PAGE_ENABLED, useLanding } from "../landingTheme";
import FreeCreditSection from "../FreeCredit";
import { usePlayLimits, limitedMessage } from "../freeCreditLogic";

const CAT_LABEL = {
  GOV: "หวยรัฐบาล / สถาบันการเงิน",
  FOREIGN: "หวยต่างประเทศ",
  STOCK: "หวยหุ้น",
  YEEKEE: "ยี่กี",
  "4D": "4D",
};
const CAT_ICON = {
  GOV: "solar:buildings-2-bold-duotone",
  FOREIGN: "solar:global-bold-duotone",
  STOCK: "solar:chart-2-bold-duotone",
  YEEKEE: "fluent-emoji-flat:alarm-clock",
  "4D": "solar:hashtag-square-bold-duotone",
};
const CAT_ORDER = ["GOV", "FOREIGN", "STOCK", "YEEKEE", "4D"];

// สลับสีป้าย/แถบหัวการ์ดตามหมวด โดยใช้แค่ 2 สีธีมเดิมของเว็บ (accent/accent-2)
const catClass = (category) => (CAT_ORDER.indexOf(category) % 2 === 0 ? "cat-a" : "cat-b");

// "ใกล้ปิดรับ": งวดที่เปิดอยู่และเหลือเวลาไม่เกินนี้ — ขยับขึ้นบนสุดของหมวด + การ์ดเรืองแสงเตือน
const CLOSING_MS = 5 * 60 * 1000;
const msToClose = (round, now) => (round ? new Date(round.close_at).getTime() - now : Infinity);
const isClosing = (round, now) => {
  const ms = msToClose(round, now);
  return ms > 0 && ms <= CLOSING_MS;
};

// สถานะการ์ด: open = เปิดแทง (เขียว) · soon = รอเปิดแทง (เหลือง) · await = ปิดรับรอออกผล (คราม) · closed = ปิดรับ (แดง)
// await แสดงถึงเวลาออกผลเท่านั้น แล้วการ์ดของงวดนั้นหายไป (ถ้ามีงวดถัดไปเปิดอยู่ จะกลายเป็นการ์ดงวดใหม่)
const STATE_META = {
  await: { label: "ปิดรับ · รอออกผล", icon: "solar:hourglass-bold", cdLabel: "ออกผลใน" },
  open: { label: "เปิดรับแทง", icon: "solar:play-circle-bold", cdLabel: "ปิดรับใน" },
  soon: { label: "รอเปิดแทง", icon: "solar:clock-circle-bold", cdLabel: "เปิดรับใน" },
  closed: { label: "ปิดรับ", icon: "solar:lock-keyhole-minimalistic-bold", cdLabel: "" },
};
// หวยที่แอดมินปิดรับแทงไว้แต่เลือกให้โชว์การ์ด (closed_card) — หน้าตาเดียวกับ closed แต่บอกว่าปิดชั่วคราว
const SUSPENDED_META = { ...STATE_META.closed, label: "ปิดรับชั่วคราว" };

function LottoCard({ type, round, upcoming, awaiting, onClick, delay = 0, fcOff = false, fcLabels, suspended = false }) {
  const closeClock = useCountdown(round?.close_at);
  const openClock = useCountdown(upcoming?.open_at);
  const resultClock = useCountdown(awaiting?.result_at);
  const isAwait = !suspended && !!awaiting && !resultClock.done;
  const isOpen = !suspended && !isAwait && !!round && !closeClock.done;
  const isSoon = !suspended && !isAwait && !isOpen && !!upcoming && !openClock.done;
  const state = suspended ? "closed" : isAwait ? "await" : isOpen ? "open" : isSoon ? "soon" : "closed";
  const meta = suspended ? SUSPENDED_META : STATE_META[state];
  const shown = isAwait ? awaiting : isOpen ? round : isSoon ? upcoming : null;
  // ใกล้ปิดรับ (≤ 5 นาที): ป้ายแดงกระพริบ + การ์ดเรืองแสงเป็นจังหวะ + แถบเวลาที่เหลือหดลงเรื่อย ๆ
  const closingMs = isOpen ? msToClose(round, Date.now()) : Infinity;
  const closing = closingMs <= CLOSING_MS;
  const resultAt = formatResultAt(shown?.result_at);
  return (
    <div
      className={`lx-lotto st-${state}${closing ? " is-closing" : ""}${fcOff ? " is-fc-off" : ""}`}
      aria-disabled={fcOff || undefined}
      onClick={() => {
        if (!isOpen) return;
        // เงินในกระเป๋าเป็นเครดิตฟรีที่ใช้หวยนี้ไม่ได้ → แจ้งแทนการเข้าหน้าแทง
        if (fcOff) return pushToast(limitedMessage(fcLabels), "err", 4500);
        onClick(round, type);
      }}
      style={{ animationDelay: `${Math.min(delay, 8) * 35}ms`, cursor: isOpen ? "pointer" : "default" }}
    >
      <div className="lx-spread">
        <FlagBadge code={type.code} category={type.category} size={44} className={catClass(type.category)} />
        {closing ? (
          <span className="lx-state-pill closing">
            <Icon icon="solar:alarm-bold" width={13} className="ring" />
            ใกล้ปิดรับ!
          </span>
        ) : (
          <span className={`lx-state-pill ${state}`}>
            {isAwait ? <Icon icon={meta.icon} width={13} className="hg" /> : <i className="dot" />}
            {meta.label}
          </span>
        )}
      </div>
      {fcOff && (
        <span className="fcoff-badge">
          <Icon icon="solar:lock-keyhole-minimalistic-bold" width={12} /> ใช้เครดิตฟรีไม่ได้
        </span>
      )}
      <div className="name">{type.name_th}</div>
      {resultAt ? (
        <div className="lx-result-when">
          <span className="lbl">
            <Icon icon="solar:cup-star-bold" width={13} />
            ผลออก
          </span>
          <span className="val">
            <b>{resultAt.isToday ? "วันนี้" : `${resultAt.day} ${resultAt.date}`}</b>
            <i>เวลา {resultAt.time} น.</i>
          </span>
        </div>
      ) : (
        shown?.round_label && <div className="foot-date">{formatRoundDate(shown.round_label)}</div>
      )}
      <div className="foot">
        <div>
          {meta.cdLabel && <div className={`lx-state-cdlabel ${state}`}>{meta.cdLabel}</div>}
        </div>
        <div className="foot-cd">
          {isAwait ? (
            <Countdown clock={resultClock} size="lg" compact warnMin={0} />
          ) : isOpen ? (
            <Countdown clock={closeClock} size="lg" compact warnMin={closing ? 6 : 3} />
          ) : isSoon ? (
            <Countdown clock={openClock} size="lg" compact />
          ) : (
            <span className="lx-state-closed-note">
              <Icon icon={meta.icon} width={14} />
              {suspended ? "ปิดรับแทงชั่วคราว" : round ? "ปิดรับแล้ว" : "ยังไม่มีงวดเปิด"}
            </span>
          )}
        </div>
      </div>
      {closing && (
        <div className="lx-closing-bar" aria-hidden="true">
          <span style={{ width: `${Math.max(0, Math.min(100, (closingMs / CLOSING_MS) * 100))}%` }} />
        </div>
      )}
    </div>
  );
}

// ผลหวยเด่น — ปักหมุดหวยรัฐบาล (หรือที่แอดมินเลือก) ให้เห็นผลล่าสุด + นับถอยหลังงวดถัดไปก่อนลงไปดูหวยชนิดอื่น
function FeaturedResult({ type, round, result, onClick }) {
  const clock = useCountdown(round?.close_at);
  const open = round && !clock.done;
  if (!type) return null;
  return (
    <section className="vx-stage">
      <div className="vx-stage-head">
        <FlagBadge code={type.code} category={type.category} size={56} />
        <div className="vx-stage-id">
          <span className="vx-kicker">
            <Icon icon="fluent-emoji-flat:trophy" width={16} height={16} />
            ผลรางวัลเด่น
          </span>
          <h2 className="vx-stage-title">{type.name_th}</h2>
          <div className="vx-stage-date">{round ? formatRoundDate(round.round_label) : "ผลล่าสุด"}</div>
        </div>
        {round && (
          <div className="vx-stage-cd">
            <div className="lx-featured-cd-label">{open ? "ปิดรับใน" : "ปิดรับแล้ว"}</div>
            <Countdown clock={clock} size="lg" />
          </div>
        )}
      </div>
      <div className="vx-stage-result">
        {result ? (
          <ResultChips result={result} />
        ) : (
          <div className="vx-stage-wait">
            <Icon icon="solar:hourglass-bold-duotone" width={18} />
            รอผลงวดถัดไป
          </div>
        )}
      </div>
      {open && (
        <button className="lx-btn mint block vx-stage-cta" onClick={onClick}>
          <Icon icon="solar:ticket-bold" width={18} />
          แทง{type.name_th}
        </button>
      )}
    </section>
  );
}

// การ์ดผลล่าสุดแบบย่อ — แถบเลื่อน "ผลรางวัลล่าสุดทุกชนิด" บนหน้าแรก
function LatestResultCard({ result, typeById }) {
  const type = typeById[result.lotto_type_id];
  return (
    <div className="vx-latest-card">
      <div className="vx-latest-head">
        <FlagBadge code={type?.code} category={type?.category} size={34} className={catClass(type?.category)} />
        <div>
          <div className="vx-latest-name">{result.type_name}</div>
          <div className="vx-latest-date">{formatRoundDate(result.round_label)}</div>
        </div>
      </div>
      <ResultChips result={result} size="sm" />
    </div>
  );
}

export default function HomePage({ balance = 0, onDone }) {
  const nav = useNavigate();
  const { isAuthenticated } = useAuth();
  // ส่วนที่แสดง / ข้อความ / ลำดับหมวด ที่แอดมินปรับได้ (admin-system → หวย → ปรับแต่งหน้าเว็บ)
  const { cfg } = useLanding();
  const { hero } = cfg;
  const limits = usePlayLimits();
  const [catSel, setCatSel] = useState("all");
  const { data: types, loading: lt } = useAsync(() => lottoApi.types(), []);
  const { data: rounds, reload: reloadRounds } = useAsync(() => lottoApi.openRounds(), []);
  const { data: upcoming, reload: reloadUpcoming } = useAsync(() => lottoApi.upcomingRounds().catch(() => []), []);
  const { data: awaitingRounds, reload: reloadAwaiting } = useAsync(
    () => lottoApi.awaitingRounds().catch(() => []),
    [],
  );

  // รีเฟรชสถานะงวดทุก 30 วิ ให้การ์ดเปลี่ยน รอเปิด → เปิด → ปิด เองโดยไม่ต้องรีโหลดหน้า
  useEffect(() => {
    const id = setInterval(() => {
      reloadRounds();
      reloadUpcoming();
      reloadAwaiting();
    }, 30000);
    return () => clearInterval(id);
  }, [reloadRounds, reloadUpcoming, reloadAwaiting]);
  const { data: recentResults } = useAsync(() => lottoApi.results({ limit: 60 }), []);

  const featuredCode = cfg.featured.typeCode;
  const featuredOn = cfg.featured.visible;
  const featuredType = useMemo(() => {
    if (!featuredOn || !types?.length) return null;
    return (
      (featuredCode && types.find((t) => t.code === featuredCode)) ||
      types.find((t) => t.code === "GOV_TH") ||
      types.find((t) => t.category === "GOV") ||
      types[0]
    );
  }, [types, featuredOn, featuredCode]);
  const { data: featuredResults } = useAsync(
    () =>
      featuredType
        ? lottoApi.results({ lottoTypeId: featuredType.id, limit: 1 })
        : Promise.resolve([]),
    [featuredType?.id],
  );

  // นาฬิกาของหน้า (ทุก 1 วิ) — จัดลำดับ "ใกล้ปิดรับ" + สลับการ์ด เปิด → รอออกผล → งวดถัดไป/หายไป ทันเวลา
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // งวดที่ยังรับแทงได้ (ใกล้ปิดสุดก่อน) — ตัดงวดที่เพิ่งปิดไปแต่ยังค้างในข้อมูลรอบ 30 วิ ออก
  const roundByType = useMemo(() => {
    const m = {};
    (rounds || []).forEach((r) => {
      if (new Date(r.close_at).getTime() <= now) return;
      if (!m[r.lotto_type_id] || new Date(r.close_at) < new Date(m[r.lotto_type_id].close_at))
        m[r.lotto_type_id] = r;
    });
    return m;
  }, [rounds, now]);

  // งวดที่ปิดรับแล้วแต่ยังไม่ถึงเวลาออกผล — จาก API + งวดที่เพิ่งปิดในข้อมูลที่โหลดไว้ (ไม่ต้องรอรีเฟรช)
  const awaitingByType = useMemo(() => {
    const m = {};
    [...(awaitingRounds || []), ...(rounds || [])].forEach((r) => {
      const close = new Date(r.close_at).getTime();
      const result = new Date(r.result_at).getTime();
      if (!(close <= now && result > now)) return;
      if (!m[r.lotto_type_id] || result < new Date(m[r.lotto_type_id].result_at).getTime()) m[r.lotto_type_id] = r;
    });
    return m;
  }, [awaitingRounds, rounds, now]);

  const upcomingByType = useMemo(() => {
    const m = {};
    (upcoming || []).forEach((r) => (m[r.lotto_type_id] = r));
    return m;
  }, [upcoming]);

  const typeById = useMemo(() => {
    const m = {};
    (types || []).forEach((t) => (m[t.id] = t));
    return m;
  }, [types]);

  // ผลล่าสุด 1 ใบ/ชนิด (API เรียงล่าสุดก่อนอยู่แล้ว) — ให้เห็นภาพรวมทุกหวยในที่เดียว
  const latestByType = useMemo(() => {
    const seen = new Map();
    (recentResults || []).forEach((r) => {
      if (!seen.has(r.lotto_type_id)) seen.set(r.lotto_type_id, r);
    });
    return [...seen.values()];
  }, [recentResults]);

  // แสดงเฉพาะหวยที่มีงวด เปิดรับ / รอเปิดแทง / รอออกผล — งวดที่ออกผลไปแล้วและไม่มีงวดถัดไป ไม่ต้องโชว์การ์ดค้าง
  // (ระหว่างโหลดรอบแรกยังไม่รู้สถานะ → แสดงทุกตัวไปก่อน)
  const roundsLoaded = rounds != null;
  const grouped = useMemo(() => {
    const g = {};
    (types || []).forEach((t) => {
      // ปิดรับแทงอยู่ (selling=false): โชว์การ์ด "ปิดรับชั่วคราว" เฉพาะที่แอดมินเปิดไว้ — ผลย้อนหลังยังแสดงตามปกติ
      if (t.selling === false) {
        if (t.closed_card) (g[t.category] = g[t.category] || []).push(t);
        return;
      }
      const live =
        roundByType[t.id] ||
        awaitingByType[t.id] ||
        (upcomingByType[t.id] && new Date(upcomingByType[t.id].open_at).getTime() > now);
      if (roundsLoaded && !live) return;
      (g[t.category] = g[t.category] || []).push(t);
    });
    return g;
  }, [types, roundByType, awaitingByType, upcomingByType, roundsLoaded, now]);
  // เรียงในหมวด: ใกล้ปิดรับก่อน (ใกล้สุดก่อน) ที่เหลือคงลำดับเดิมที่แอดมินตั้ง
  const sortedInCat = (cat) =>
    grouped[cat]
      .map((t, i) => ({ t, i, ms: isClosing(roundByType[t.id], now) ? msToClose(roundByType[t.id], now) : Infinity }))
      .sort((a, b) => (a.t.selling === false) - (b.t.selling === false) || a.ms - b.ms || a.i - b.i) // ปิดรับชั่วคราวไว้ท้ายหมวด
      .map((x) => x.t);
  const closingCount = (cat) => grouped[cat].filter((t) => isClosing(roundByType[t.id], now)).length;

  const go = (round, type) => {
    if (type.category === "YEEKEE") nav(`/yeekee/${round.id}`);
    else nav(`/play/${round.id}`);
  };

  const heroCopy = (
    <>
      {hero.eyebrow && (
        <span className="vx-eyebrow">
          <Icon icon={hero.eyebrowIcon} width={14} />
          {hero.eyebrow}
        </span>
      )}
      {(hero.title || hero.titleHighlight) && (
        <h1 className="vx-title">
          {hero.title} {hero.titleHighlight && <b>{hero.titleHighlight}</b>}
        </h1>
      )}
      {hero.subtitle && <p className="vx-sub">{hero.subtitle}</p>}
      {!!hero.badges.length && (
        <div className="vx-badges">
          {hero.badges.map((b) => (
            <span className="vx-badge" key={`${b.icon}-${b.label}`}>
              <Icon className="ic" icon={b.icon} />
              {b.label}
            </span>
          ))}
        </div>
      )}
      <div className="vx-hero-cta">
        <button
          type="button"
          className="lx-btn mint"
          onClick={() => document.getElementById("vx-lotto")?.scrollIntoView({ behavior: "smooth", block: "start" })}
        >
          <Icon icon="solar:ticket-bold" width={18} />
          เลือกหวยที่เปิดอยู่
        </button>
        {RESULTS_PAGE_ENABLED && (
          <button type="button" className="lx-btn vx-glass" onClick={() => nav("/results")}>
            <Icon icon="solar:cup-star-bold" width={18} />
            ดูผลหวย
          </button>
        )}
      </div>
    </>
  );

  // รูปพื้นหลังการ์ดต้อนรับ (แอดมินใส่) — path /uploads/... ต่อด้วย base ของ API เหมือนโลโก้
  const heroImg = hero.bgImage
    ? hero.bgImage.startsWith("http")
      ? hero.bgImage
      : `${lottoApi.base}${hero.bgImage}`
    : "";
  const heroStyle = heroImg ? { "--lx-hero-img": `url("${heroImg}")` } : undefined;

  // ช่องด้านข้างของการ์ดต้อนรับ: ผู้เยี่ยมชม = ฟอร์มเข้าสู่ระบบ/สมัคร · สมาชิก = กระเป๋าเงินย่อ
  const heroSide = isAuthenticated ? (
    <div className="vx-mini-wallet">
      <div className="vx-mw-label">
        <Icon icon="fluent-emoji-flat:coin" width={20} height={20} />
        ยอดเงินคงเหลือ
      </div>
      <div className="vx-mw-bal">
        <span className="lx-mono">{money2(balance)}</span>
        <small>บาท</small>
      </div>
      <div className="vx-mw-actions">
        <button type="button" className="lx-btn mint block" onClick={() => nav("/deposit")}>
          <Icon icon="solar:download-minimalistic-bold" width={18} />
          ฝากเงิน
        </button>
        <button type="button" className="lx-btn vx-glass block" onClick={() => nav("/withdraw")}>
          <Icon icon="solar:upload-minimalistic-bold" width={18} />
          ถอนเงิน
        </button>
      </div>
    </div>
  ) : hero.showAuthCard ? (
    <div className="lx-card lx-auth-card vx-auth">
      <AuthForm compact />
    </div>
  ) : null;

  // หมวดหวย: ลำดับ/การซ่อน/ชื่อ ตามที่แอดมินตั้ง (หมวดที่ไม่มีหวยจะไม่แสดงอยู่แล้ว)
  const catList = cfg.categories.filter((c) => c.visible && grouped[c.key]?.length);
  const activeCat = catList.some((c) => c.key === catSel) ? catSel : "all";
  const shownCats = activeCat === "all" ? catList : catList.filter((c) => c.key === activeCat);

  return (
    <div className="vx-home">
      {hero.visible ? (
        <section className={`vx-hero${heroImg ? " has-img" : ""}${heroSide ? " has-side" : ""}`} style={heroStyle}>
          <div className="vx-hero-art" aria-hidden="true">
            <Icon icon="fluent-emoji-flat:trophy" className="a1" />
            <Icon icon="fluent-emoji-flat:money-bag" className="a2" />
            <Icon icon="fluent-emoji-flat:four-leaf-clover" className="a3" />
            <Icon icon="fluent-emoji-flat:gem-stone" className="a4" />
          </div>
          <div className="vx-hero-copy">{heroCopy}</div>
          {heroSide && <div className="vx-hero-side">{heroSide}</div>}
        </section>
      ) : (
        !isAuthenticated &&
        hero.showAuthCard && (
          // ซ่อนการ์ดต้อนรับแต่ยังให้ฟอร์มเข้าสู่ระบบ — จัดกึ่งกลางหน้า
          <div className="lx-card lx-auth-card lx-auth-solo">
            <AuthForm compact />
          </div>
        )
      )}

      {cfg.banner.visible && (
        <div className="vx-banner">
          <Slider className="lx-slider-full" />
        </div>
      )}

      {/* เครดิตฟรี — วางเหนือผลหวยรัฐบาลไทย */}
      <FreeCreditSection
        onClaimed={(d) => {
          onDone?.(d);
          limits.reload();
        }}
      />

      <FeaturedResult
        type={featuredType}
        round={roundByType[featuredType?.id]}
        result={featuredResults?.[0]}
        onClick={() => featuredType && go(roundByType[featuredType.id], featuredType)}
      />

      {cfg.latest.visible && !!latestByType.length && (
        <section className="vx-block">
          <div className="vx-block-head">
            <div className="vx-block-title">
              <span className="vx-block-ic cat-b">
                <Icon icon="solar:ranking-bold-duotone" width={18} height={18} />
              </span>
              <h2 className="lx-h2">{cfg.latest.title}</h2>
            </div>
            {RESULTS_PAGE_ENABLED && <a href="/results">ดูทั้งหมด →</a>}
          </div>
          <div className="vx-latest-strip">
            {latestByType.map((r) => (
              <LatestResultCard key={`latest-${r.lotto_type_id}`} result={r} typeById={typeById} />
            ))}
          </div>
        </section>
      )}

      <div id="vx-lotto" className="vx-anchor" />

      {catList.length > 1 && (
        <div className="vx-cats" role="tablist" aria-label="หมวดหวย">
          <button
            type="button"
            role="tab"
            aria-selected={activeCat === "all"}
            className={activeCat === "all" ? "on" : ""}
            onClick={() => setCatSel("all")}
          >
            <Icon icon="solar:widget-5-bold-duotone" width={17} height={17} />
            ทั้งหมด
          </button>
          {catList.map((c) => (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={activeCat === c.key}
              className={activeCat === c.key ? "on" : ""}
              onClick={() => setCatSel(c.key)}
            >
              <Icon icon={CAT_ICON[c.key] || "solar:ticket-bold-duotone"} width={17} height={17} />
              {c.label || CAT_LABEL[c.key] || c.key}
              <span className="vx-cats-n">{grouped[c.key].length}</span>
            </button>
          ))}
        </div>
      )}

      {lt && (
        <div className="lx-grid" style={{ marginTop: 22 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} h={128} />
          ))}
        </div>
      )}

      {shownCats.map(({ key: cat, label: catLabel }) => (
        <section className="vx-block" key={cat}>
          <div className="vx-block-head">
            <div className="vx-block-title">
              <span className={`vx-block-ic ${catClass(cat)}`}>
                <Icon icon={CAT_ICON[cat] || "solar:ticket-bold-duotone"} width={18} height={18} />
              </span>
              <h2 className="lx-h2">{catLabel || CAT_LABEL[cat] || cat}</h2>
              <span className="lx-count-badge">{grouped[cat].length}</span>
              {closingCount(cat) > 0 && (
                <span className="lx-closing-count">
                  <Icon icon="solar:alarm-bold" width={12} />
                  ใกล้ปิด {closingCount(cat)}
                </span>
              )}
            </div>
            {/* เมนูหลักเปลี่ยนจากยี่กีเป็นแนะนำเพื่อน — ทางเข้าหน้ารวมยี่กีย้ายมาอยู่ตรงนี้แทน */}
            {cat === "YEEKEE" && (
              <a
                href="/yeekee"
                onClick={(e) => {
                  e.preventDefault();
                  nav("/yeekee");
                }}
              >
                ดูทั้งหมด
              </a>
            )}
          </div>
          <div className={`lx-grid ${cat === "STOCK" || cat === "YEEKEE" ? "g4" : ""}`}>
            {sortedInCat(cat).map((t, i) => (
              <LottoCard
                key={t.id}
                type={t}
                round={roundByType[t.id]}
                upcoming={upcomingByType[t.id]}
                awaiting={awaitingByType[t.id]}
                onClick={go}
                delay={i}
                fcOff={t.selling !== false && limits.blocked.has(t.id)}
                fcLabels={limits.labels}
                suspended={t.selling === false}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
